/**
 * KingRivet.ts
 * World 3 boss — Brasscloud Foundry. Aerial mech with destructible
 * armour plates.
 *
 * Behaviour: hovers in place above the arena, oscillating horizontally
 * around a fixed centre. Four armour plates protect the core; while
 * any plate remains intact the boss is in the ARMOURED phase and shard
 * hits are blocked outside a periodic telegraph window (TELL). During
 * TELL each plate takes 1 HP of damage. Once the last plate is
 * destroyed the boss transitions to EXPOSED (the cycle stops and every
 * hit damages the core, 6 HP).
 *
 * Total HP pool: 4 plates × 1 HP + 6 core = 10 (matches ROADMAP).
 *
 * State machine:
 *   ARMOURED_HOVERING ↔ ARMOURED_TELL  (loop while plates > 0)
 *                    → EXPOSED_HOVERING (when plates → 0)
 *                    → DEAD             (when core HP → 0)
 *
 * The actual rivet projectile fired during/after TELL is deferred to the
 * level combat layer via a single-tick `fireRequested` pulse on TELL exit
 * (same pattern as `SparkImp` and `TideMirror`).
 *
 * Rules (AGENTS.md):
 *   - No React / Skia / async imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 3 boss greybox stub. Wired into levels when W3 ships.
 * See ROADMAP.md Phase 4 (`KingRivet.ts`).
 *
 * @module KingRivet
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import Logger from '../../utils/logger';

export type KingRivetState =
  | 'ARMOURED_HOVERING'
  | 'ARMOURED_TELL'
  | 'EXPOSED_HOVERING'
  | 'DEAD';

/**
 * Projectile-impact result. `BLOCKED` while plates are up outside a TELL
 * window; `HURT` during TELL (against plates) and any time during EXPOSED
 * (against the core).
 */
export type KingRivetHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/** Boss hitbox dimensions in logical pixels — large aerial silhouette. */
export const KING_RIVET_WIDTH = 48;
export const KING_RIVET_HEIGHT = 40;
/** Greybox render colour — brass plate. */
export const KING_RIVET_COLOR = '#c08038';

/** Number of destructible armour plates. */
export const KING_RIVET_PLATES = 4;
/** Core HP once plates are destroyed. */
export const KING_RIVET_CORE_HP = 6;
/** Convenience constant — total combined HP pool. */
export const KING_RIVET_MAX_HP = KING_RIVET_PLATES + KING_RIVET_CORE_HP;

/** Time between rivet tells while plates remain. */
const TELL_CYCLE = 5.0;
/** Telegraph length — also the plate-vulnerability window. */
const TELL_DURATION = 1.0;

/** Horizontal hover oscillation amplitude (px) and period (s). */
const HOVER_AMPLITUDE_X = 80;
const HOVER_PERIOD = 4.0;
const HOVER_OMEGA = (2 * Math.PI) / HOVER_PERIOD;

/**
 * KingRivet — World 3 boss (Brasscloud Foundry).
 *
 * Spawn with `new KingRivet(centerX, centerY)` — those coordinates are
 * the hover anchor; the boss oscillates around them. Call
 * `update(dt, level, nova?)` each fixed tick. Read `fireRequested` for
 * the single-tick rivet pulse, `dead` to know when to remove, and
 * `platesRemaining` / `coreHp` for HUD purposes.
 */
export class KingRivet extends BaseEntity {
  public readonly entityType = 'KingRivet';
  public readonly color = KING_RIVET_COLOR;
  public readonly maxHp = KING_RIVET_MAX_HP;
  public state: KingRivetState = 'ARMOURED_HOVERING';

  /**
   * True once dead. KingRivet has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** Intact armour-plate count. Decremented by hits landed during TELL. */
  public platesRemaining = KING_RIVET_PLATES;
  /** Core HP — only damaged once `platesRemaining === 0`. */
  public coreHp = KING_RIVET_CORE_HP;
  /** True when facing right (renderer hook — fixed for greybox). */
  public facingRight = false;
  /** Single-tick pulse — true on the tick the boss exits TELL. */
  public fireRequested = false;
  /** Hover anchor — boss oscillates horizontally around this x. */
  private readonly _centerX: number;
  /** Hover anchor y — boss is aerial and stays here (no gravity). */
  private readonly _centerY: number;
  /** Cycle timers. */
  private _tellTimer = 0;
  private _cycleTimer = TELL_CYCLE;
  /** Phase accumulator for the hover oscillation. */
  private _hoverPhase = 0;

  constructor(centerX: number, centerY: number) {
    super(centerX, centerY, KING_RIVET_WIDTH, KING_RIVET_HEIGHT);
    this._centerX = centerX;
    this._centerY = centerY;
  }

  /**
   * Total combined HP across plates and core (HUD convenience).
   * @returns plates + coreHp.
   */
  public get hp(): number {
    return this.platesRemaining + this.coreHp;
  }

  /**
   * Advance the boss by one fixed tick. `nova` is accepted for contract
   * compatibility with the shared `Enemy` interface but is unused — the
   * boss is autonomous and the level layer consumes `fireRequested` to
   * orchestrate attacks aimed at Nova.
   *
   * @param dt    Fixed-step delta in seconds.
   * @param _level Current level geometry — unused (aerial; no collision).
   * @param _nova Optional Nova runtime — unused.
   */
  public update(dt: number, _level: LevelRuntime, _nova?: NovaRuntime): void {
    // DECISION: Clear the pulse at the top of every tick; the exit
    // transition re-asserts it the same tick it fires. One-tick pulse
    // matches SparkImp / TideMirror.
    this.fireRequested = false;
    if (this.state === 'DEAD') return;

    this._updateHover(dt);

    switch (this.state) {
      case 'ARMOURED_HOVERING':
        this._cycleTimer -= dt;
        if (this._cycleTimer <= 0) this._enterTell();
        break;
      case 'ARMOURED_TELL':
        this._tellTimer -= dt;
        if (this._tellTimer <= 0) this._exitTell();
        break;
      case 'EXPOSED_HOVERING':
        // No cycle — core is exposed permanently until dead.
        break;
    }
  }

  /**
   * Drive the sinusoidal hover. x oscillates around `_centerX` with
   * amplitude `HOVER_AMPLITUDE_X` and period `HOVER_PERIOD`. y is locked
   * to `_centerY` (aerial mech — no gravity).
   */
  private _updateHover(dt: number): void {
    this._hoverPhase += dt;
    this.x = this._centerX + Math.sin(this._hoverPhase * HOVER_OMEGA) * HOVER_AMPLITUDE_X;
    this.y = this._centerY;
    this.vx = 0;
    this.vy = 0;
  }

  private _enterTell(): void {
    this.state = 'ARMOURED_TELL';
    this._tellTimer = TELL_DURATION;
    Logger.debug('KingRivet', 'Rivet tell → plates vulnerable');
  }

  private _exitTell(): void {
    this.state = 'ARMOURED_HOVERING';
    this._cycleTimer = TELL_CYCLE;
    this.fireRequested = true; // single-tick pulse on tell exit
    Logger.debug('KingRivet', 'Tell complete → rivet fires');
  }

  /**
   * Apply one projectile hit.
   * @returns `'BLOCKED'` while ARMOURED_HOVERING, `'HURT'` while
   *          ARMOURED_TELL or EXPOSED_HOVERING, `'IGNORED'` once DEAD.
   */
  public applyShardHit(): KingRivetHitResult {
    if (this.state === 'DEAD') return 'IGNORED';
    if (this.state === 'ARMOURED_HOVERING') return 'BLOCKED';
    this._takeDamage();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. Armoured outside TELL while plates remain.
   * @returns `true` when the stomp connected; `false` otherwise.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD') return false;
    if (this.state === 'ARMOURED_HOVERING') return false;
    this._takeDamage();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts).
   * @param fromStomp `true` for stomp contact, `false`/omitted for shards.
   * @returns `true` if this hit dealt damage; `false` if blocked or ignored.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    return this.applyShardHit() === 'HURT';
  }

  /**
   * Deduct one HP from the relevant pool and route phase transitions.
   * Called only from a successful hit (TELL on plates, or EXPOSED on core).
   */
  private _takeDamage(): void {
    if (this.platesRemaining > 0) {
      this.platesRemaining -= 1;
      Logger.debug(
        'KingRivet',
        `Plate destroyed — ${this.platesRemaining}/${KING_RIVET_PLATES} remaining`,
      );
      if (this.platesRemaining === 0) {
        this.state = 'EXPOSED_HOVERING';
        Logger.debug('KingRivet', 'All plates down → EXPOSED');
      }
      return;
    }
    this.coreHp -= 1;
    Logger.debug('KingRivet', `Core hit — coreHp ${this.coreHp}/${KING_RIVET_CORE_HP}`);
    if (this.coreHp <= 0) {
      this.state = 'DEAD';
      this.dead = true;
      Logger.debug('KingRivet', 'Defeated');
    }
  }
}
